import { TFunction } from 'i18next';
import DOMPurify from 'dompurify';
import { CheckCircle, XCircle, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { DataTableColumn, TableAction } from '@/shared/ui/data-table';
import { SERVICE_REQUEST_STATUS_LABELS, getServiceRequestActionPermissions } from '@/entities/service-request';

import { ServiceRequestTableData } from './types';

export const getColumns = (t: TFunction): DataTableColumn<ServiceRequestTableData>[] => [
  {
    id: 'createdTime',
    accessorKey: 'createdTime',
    header: t('request.createdTime.short'),
    description: 'Creation date',
    width: 'min-w-[100px]',
    cell: (row) => {
      const date = new Date(row.createdTime);
      return <div className="text-xs text-muted-foreground">{date.toLocaleDateString()}</div>;
    },
    enableSorting: true,
    sortFieldId: 'createdTime'
  },
  {
    id: 'trackIdnf',
    accessorKey: 'trackIdnf',
    header: t('request.trackIdnf.label'),
    description: 'Public tracking identifier',
    width: 'min-w-[120px]',
    cell: (row) => <span className="font-mono text-sm font-medium text-primary">{row.trackIdnf}</span>,
    enableSorting: true,
    sortFieldId: 'trackIdnf'
  },
  {
    id: 'statusCode',
    accessorKey: 'statusCode',
    header: t('request.status.label'),
    description: 'Request status',
    width: 'min-w-[100px]',
    cell: (row) => {
      const getVariant = (code: string) => {
        switch (code) {
          case 'P':
            return 'warning';
          case 'V':
            return 'default';
          case 'A':
            return 'success';
          case 'R':
            return 'destructive';
          default:
            return 'secondary';
        }
      };
      return <Badge variant={getVariant(row.statusCode)}>{SERVICE_REQUEST_STATUS_LABELS[row.statusCode]}</Badge>;
    },
    enableSorting: true,
    sortFieldId: 'statusCode'
  },
  {
    id: 'serviceIdentification',
    accessorKey: 'serviceIdentification',
    header: t('request.serviceIdent.label'),
    description: 'Service type and location',
    width: 'min-w-[200px] max-w-[250px] whitespace-normal',
    cell: (row) => {
      if (!row.serviceIdentification) {
        return '-';
      }
      const sanitizedHtml = DOMPurify.sanitize(row.serviceIdentification, {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br', 'span', 'p'],
        ALLOWED_ATTR: ['class', 'style']
      });
      return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    },
    enableSorting: false
  },
  {
    id: 'senderDisplay',
    accessorKey: 'senderDisplay',
    header: t('request.sender.label'),
    description: 'Sender organization and contact',
    width: 'min-w-[150px] max-w-[200px] whitespace-normal',
    cell: (row) => row.senderDisplay || '-',
    enableSorting: true,
    sortFieldId: 'senderOrgName'
  },
  {
    id: 'receiverDisplay',
    accessorKey: 'receiverDisplay',
    header: t('request.receiver.label'),
    description: 'Receiver organization and contact',
    width: 'min-w-[150px] max-w-[200px] whitespace-normal',
    cell: (row) => row.receiverDisplay || '-',
    enableSorting: true,
    sortFieldId: 'receiverOrgName'
  },
  {
    id: 'totalWeight',
    accessorKey: 'totalWeight',
    header: t('request.totalWeight.label'),
    description: 'Total weight in kilograms',
    width: 'min-w-[100px]',
    cell: (row) => <div className="text-right">{row.totalWeight ? row.totalWeight.toFixed(1) : '-'}</div>,
    enableSorting: false
  },
  {
    id: 'totalVolume',
    accessorKey: 'totalVolume',
    header: t('request.totalVolume.label'),
    description: 'Total volume in cubic meters',
    width: 'min-w-[100px]',
    cell: (row) => <div className="text-right">{row.totalVolume ? row.totalVolume.toFixed(1) : '-'}</div>,
    enableSorting: false
  },
  {
    id: 'overpackingSummary',
    accessorKey: 'overpackingSummary',
    header: t('tracking.servicereq.overpackingqty.label'),
    description: 'Overpacking units summary',
    width: 'min-w-[150px] max-w-[200px] whitespace-normal',
    cell: (row) => row.overpackingSummary || '-',
    enableSorting: false
  },
  {
    id: 'items',
    accessorKey: 'items',
    header: t('newcmr.items.label'),
    description: 'Number of line items',
    width: 'min-w-[80px]',
    cell: (row) => {
      const itemCount = row.items?.length || 0;
      return <div className="text-center">{itemCount > 0 ? <Badge variant="secondary">{itemCount}</Badge> : '-'}</div>;
    },
    enableSorting: false
  },
  {
    id: 'smpTicketNumber',
    accessorKey: 'smpTicketNumber',
    header: t('newcmr.smpTicketNumber.label'),
    description: 'SMP ticket reference',
    width: 'min-w-[120px]',
    cell: (row) => <span className="font-mono text-sm">{row.smpTicketNumber || '-'}</span>,
    enableSorting: false
  }
];

export const getServiceRequestActions = (
  t: TFunction,
  handlers: {
    onMoveToConsignment: (row: ServiceRequestTableData) => void;
    onReject: (row: ServiceRequestTableData) => void;
    onPrintForm: (row: ServiceRequestTableData) => void;
  },
  isReadOnly: boolean,
  printingIds: string[]
): TableAction<ServiceRequestTableData>[] => [
  {
    id: 'moveToConsignment',
    label: t('cmrlist.accept.onlinesrf.label', 'Move to Consignment'),
    icon: CheckCircle,
    isHidden: (row) => !getServiceRequestActionPermissions(row.actions, isReadOnly).canMoveToConsignment,
    onClick: handlers.onMoveToConsignment
  },
  {
    id: 'reject',
    label: t('cmrlist.reject.onlinesrf.label', 'Reject Online SRF Form'),
    icon: XCircle,
    isHidden: (row) => !getServiceRequestActionPermissions(row.actions, isReadOnly).canReject,
    onClick: handlers.onReject
  },
  {
    id: 'print',
    label: t('cmrlist.print.onlinesrf.label', 'Print Online Service Request Form (en)'),
    icon: FileText,
    isHidden: (row) => !getServiceRequestActionPermissions(row.actions, isReadOnly).canPrint,
    isDisabled: (row) => printingIds.includes(row.id),
    onClick: handlers.onPrintForm
  }
];
